import React, { useState, useEffect, useMemo } from 'react';
import { logService, LogEntry, LogLevel, LogCategory } from '../services/logService';
import './LogsPage.css';

// Icone SVG
const IconTrash = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3,6 5,6 21,6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const IconDownload = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const IconRefresh = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const IconChevron = ({ expanded }: { expanded: boolean }) => (
  <svg 
    width="16" 
    height="16" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const IconWarning = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const IconError = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>
);

const IconSuccess = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const IconDebug = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
    <path d="M10 14a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"></path>
    <path d="M16 14a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"></path>
  </svg>
);

const IconSearch = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const IconClear = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const IconList = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

const IconFilter = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const getLevelIcon = (level: LogLevel) => {
  switch (level) {
    case 'info': return <IconInfo />;
    case 'warn': return <IconWarning />;
    case 'error': return <IconError />;
    case 'success': return <IconSuccess />;
    case 'debug': return <IconDebug />;
    default: return <IconInfo />;
  }
};

const getLevelLabel = (level: LogLevel): string => {
  switch (level) {
    case 'info': return 'Info';
    case 'warn': return 'Avviso';
    case 'error': return 'Errore';
    case 'success': return 'Successo';
    case 'debug': return 'Debug';
    default: return level;
  }
};

const LogsPage: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState<LogLevel | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = () => {
    setLogs(logService.getAll());
    setSelectedIds(new Set());
    setSelectMode(false);
  };

  const handleClearAll = () => {
    if (window.confirm('Sei sicuro di voler eliminare tutti i log? Questa azione non pu� essere annullata.')) {
      logService.clear();
      loadLogs();
    }
  };

  const handleDeleteSingle = (id: string) => {
    if (window.confirm('Eliminare questo log?')) {
      logService.delete(id);
      loadLogs();
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) return;
    
    if (window.confirm(`Eliminare ${selectedIds.size} log selezionati?`)) {
      selectedIds.forEach(id => {
        logService.delete(id);
      });
      loadLogs();
    }
  };

  const handleDownload = () => {
    logService.downloadLogs();
  };

  const handleToggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLogs.map(log => log.id)));
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      setSelectedIds(new Set());
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDate = (timestamp: string): string => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Oggi';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ieri';
    } else {
      return date.toLocaleDateString('it-IT', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
    }
  };

  // Filtro e raggruppamento dei log
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (filter !== 'all' && log.level !== filter) return false;
      if (categoryFilter !== 'all' && log.category !== categoryFilter) return false;
      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          log.message.toLowerCase().includes(search) ||
          log.category.toLowerCase().includes(search) ||
          (log.details && log.details.toLowerCase().includes(search))
        );
      }
      return true;
    });
  }, [logs, filter, categoryFilter, searchText]);

  // Raggruppa per data
  const groupedLogs = useMemo(() => {
    const groups: { [key: string]: LogEntry[] } = {};
    filteredLogs.forEach(log => {
      const dateKey = new Date(log.timestamp).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(log);
    });
    return groups;
  }, [filteredLogs]);

  const categories = useMemo(() => {
    return Array.from(new Set(logs.map(log => log.category)));
  }, [logs]);

  // Statistiche
  const stats = useMemo(() => ({
    total: logs.length,
    errors: logs.filter(l => l.level === 'error').length,
    warnings: logs.filter(l => l.level === 'warn').length,
    info: logs.filter(l => l.level === 'info').length,
    success: logs.filter(l => l.level === 'success').length,
  }), [logs]);

  return (
    <div className="logs-page">
      {/* Header */}
      <header className="logs-header">
        <div className="logs-title-section">
          <IconList />
          <h1>Registro Log</h1>
        </div>
        <p className="logs-subtitle">Visualizza e gestisci i log dell'applicazione</p>
      </header>

      {/* Stats Cards */}
      <div className="logs-stats">
        <div className="stat-card stat-total">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Totali</span>
        </div>
        <div className="stat-card stat-error" onClick={() => setFilter(filter === 'error' ? 'all' : 'error')}>
          <span className="stat-value">{stats.errors}</span>
          <span className="stat-label">Errori</span>
        </div>
        <div className="stat-card stat-warn" onClick={() => setFilter(filter === 'warn' ? 'all' : 'warn')}>
          <span className="stat-value">{stats.warnings}</span>
          <span className="stat-label">Avvisi</span>
        </div>
        <div className="stat-card stat-success" onClick={() => setFilter(filter === 'success' ? 'all' : 'success')}>
          <span className="stat-value">{stats.success}</span>
          <span className="stat-label">Successo</span>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="logs-filters">
        <div className="search-box">
          <IconSearch />
          <input
            type="text"
            placeholder="Cerca nei log..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button className="search-clear" onClick={() => setSearchText('')}>
              <IconClear />
            </button>
          )}
        </div>

        <div className="filter-row">
          <select 
            value={filter} 
            onChange={e => setFilter(e.target.value as LogLevel | 'all')}
            className="filter-select"
          >
            <option value="all">Tutti i livelli</option>
            <option value="error">Errori</option>
            <option value="warn">Avvisi</option>
            <option value="info">Info</option>
            <option value="success">Successi</option>
            <option value="debug">Debug</option>
          </select>

          <select 
            value={categoryFilter} 
            onChange={e => setCategoryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tutte le categorie</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="logs-actions">
        <div className="actions-left">
          <button 
            className={`btn-action btn-select ${selectMode ? 'active' : ''}`}
            onClick={toggleSelectMode}
          >
            {selectMode ? 'Annulla' : 'Seleziona'}
          </button>
          
          {selectMode && (
            <>
              <button 
                className="btn-action btn-select-all"
                onClick={handleSelectAll}
              >
                {selectedIds.size === filteredLogs.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </button>
              {selectedIds.size > 0 && (
                <button 
                  className="btn-action btn-delete-selected"
                  onClick={handleDeleteSelected}
                >
                  <IconTrash />
                  Elimina ({selectedIds.size})
                </button>
              )}
            </>
          )}
        </div>

        <div className="actions-right">
          <button 
            className="btn-action btn-refresh" 
            onClick={loadLogs}
            title="Aggiorna"
          >
            <IconRefresh />
          </button>
          <button 
            className="btn-action btn-download" 
            onClick={handleDownload}
            disabled={logs.length === 0}
            title="Scarica log"
          >
            <IconDownload />
            <span className="btn-text">Esporta</span>
          </button>
          <button 
            className="btn-action btn-clear-all" 
            onClick={handleClearAll}
            disabled={logs.length === 0}
            title="Elimina tutti i log"
          >
            <IconTrash />
            <span className="btn-text">Svuota</span>
          </button>
        </div>
      </div>

      {/* Filtered results info */}
      {(filter !== 'all' || categoryFilter !== 'all' || searchText) && (
        <div className="filter-info">
          <span className="filter-info-content">
            <IconFilter />
            <span>
              {filteredLogs.length} risultati
              {filter !== 'all' && ` | Livello: ${getLevelLabel(filter as LogLevel)}`}
              {categoryFilter !== 'all' && ` | Categoria: ${categoryFilter}`}
              {searchText && ` | Ricerca: "${searchText}"`}
            </span>
          </span>
          <button 
            className="btn-reset-filters"
            onClick={() => {
              setFilter('all');
              setCategoryFilter('all');
              setSearchText('');
            }}
          >
            Rimuovi filtri
          </button>
        </div>
      )}

      {/* Log List */}
      <div className="logs-list">
        {filteredLogs.length === 0 ? (
          <div className="empty-logs">
            {logs.length === 0 ? (
              <>
                <IconList />
                <p>Nessun log registrato</p>
              </>
            ) : (
              <>
                <IconSearch />
                <p>Nessun risultato</p>
                <span>Prova a modificare i filtri di ricerca</span>
              </>
            )}
          </div>
        ) : (
          Object.entries(groupedLogs).map(([dateKey, dateLogs]) => (
            <div key={dateKey} className="log-group">
              <div className="log-group-header">
                <span className="log-group-date">{formatDate(dateLogs[0].timestamp)}</span>
                <span className="log-group-count">{dateLogs.length} log</span>
              </div>
              
              {dateLogs.map(log => (
                <div 
                  key={log.id} 
                  className={`log-entry log-${log.level} ${expandedIds.has(log.id) ? 'expanded' : ''} ${selectedIds.has(log.id) ? 'selected' : ''}`}
                >
                  <div className="log-entry-main">
                    {selectMode && (
                      <label className="log-checkbox">
                        <input 
                          type="checkbox" 
                          checked={selectedIds.has(log.id)}
                          onChange={() => handleToggleSelect(log.id)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    )}
                    
                    <div 
                      className="log-entry-header"
                      onClick={() => (log.details || log.stack) && handleToggleExpand(log.id)}
                    >
                      <div className="log-entry-icon">
                        {getLevelIcon(log.level)}
                      </div>
                      <div className="log-entry-content">
                        <div className="log-entry-top">
                          <span className="log-category">{log.category}</span>
                          <span className="log-timestamp">
                            {new Date(log.timestamp).toLocaleTimeString('it-IT')}
                          </span>
                        </div>
                        <span className="log-message">{log.message}</span>
                        <div className="log-entry-meta">
                          <span className={`log-level-badge level-${log.level}`}>
                            {getLevelLabel(log.level)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="log-entry-actions">
                        {(log.details || log.stack) && (
                          <button className="btn-expand">
                            <IconChevron expanded={expandedIds.has(log.id)} />
                          </button>
                        )}
                        {!selectMode && (
                          <button 
                            className="btn-delete-single"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSingle(log.id);
                            }}
                            title="Elimina log"
                          >
                            <IconTrash />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedIds.has(log.id) && (log.details || log.stack) && (
                    <div className="log-entry-details">
                      {log.details && (
                        <div className="log-detail-section">
                          <strong>Dettagli:</strong>
                          <pre>{log.details}</pre>
                        </div>
                      )}
                      {log.stack && (
                        <div className="log-detail-section">
                          <strong>Stack trace:</strong>
                          <pre className="stack-trace">{log.stack}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LogsPage;
